import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, Gauge, KeyRound, LogOut, Settings } from "lucide-react";
import { formatQuotaReset } from "./UsagePanel";
import "./AccountExperience.css";

export default function AccountMenu({ user, isDark, anchorRef, webUsage, apiUsage, onClose, onProfile, onSettings, onLogout }) {
  const [expanded, setExpanded] = useState(false);
  const [position, setPosition] = useState({ left: 12, bottom: 70 });
  const menuRef = useRef(null);
  const name = user?.username || "我的账户";
  const current = [webUsage, apiUsage].filter(Boolean).sort((a,b) => (b.server_now || 0)-(a.server_now || 0))[0];
  const weekly = current?.weekly;
  const exhausted = weekly?.allowed === false;
  const percent = (value) => !Number.isFinite(value?.remaining_percent) ? "--" : exhausted ? "0%" : value.remaining_percent > 0 && value.remaining_percent < 1 ? "<1%" : `${Math.round(value.remaining_percent)}%`;
  useEffect(() => {
    const anchor = anchorRef.current;
    const place = () => {
      const rect = anchor?.getBoundingClientRect();
      const width = Math.min(264, window.innerWidth - 24);
      if (rect) setPosition({ width, left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), bottom: Math.max(12, window.innerHeight - rect.top + 8), maxHeight: Math.max(100,rect.top-20) });
    };
    place();
    menuRef.current?.querySelector("button")?.focus();
    const key = (event) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); anchor?.focus(); }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const buttons = [...menuRef.current.querySelectorAll("button")];
        const index = buttons.indexOf(document.activeElement);
        event.preventDefault(); buttons[(index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length]?.focus();
      }
    };
    window.addEventListener("resize",place);
    menuRef.current?.addEventListener("keydown",key);
    const menu = menuRef.current;
    return () => { window.removeEventListener("resize",place); menu?.removeEventListener("keydown",key); };
  }, [anchorRef, onClose]);
  const open = (tab) => { onClose(); onSettings(tab); };
  return createPortal(<div className={`account-menu-layer ${isDark ? "theme-dark" : "theme-light"}`}>
    <div className="account-menu-dismiss" onPointerDown={onClose} />
    <div className="account-menu" ref={menuRef} role="dialog" aria-label="账号菜单" style={position}>
      <button className="account-menu-profile" type="button" onClick={() => { onClose(); onProfile(); }}>
        <span className="account-avatar">{name.trim()[0]}</span><span><strong>{name}</strong><small>{webUsage?.membership_tier || "Free"}</small></span><ChevronRight size={16} />
      </button>
      <hr />
      <button type="button" className="account-menu-item" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}><Gauge size={18} /><span>剩余用量</span><ChevronDown size={16} className={expanded ? "rotated" : ""} /></button>
      {expanded && <div className="account-menu-usage">
        {[{ label:"总额度",value:weekly,period:"weekly",now:current?.server_now }, {label:"网页对话",value:webUsage?.chat,period:"lane",now:webUsage?.server_now}, {label:"API 调用",value:apiUsage?.api_token,period:"lane",now:apiUsage?.server_now}].map(row =>
          <div className="account-usage-line" key={row.label}><span>{row.label}</span><strong>{percent(row.value)}</strong><time>{formatQuotaReset(row.value?.reset_at,row.period,row.now)}</time></div>)}
        <div className="account-usage-line"><span>图片额度</span><strong>{Number.isFinite(webUsage?.image?.remaining_percent) ? `${Math.round(webUsage.image.remaining_percent)}%` : "--"}</strong><time>每日重置</time></div>
        <button type="button" onClick={() => open("billing")}>查看完整用量<ChevronRight size={14} /></button>
      </div>}
      <button type="button" className="account-menu-item" onClick={() => open("keys")}><KeyRound size={18} /><span>密钥管理</span><ChevronRight size={16} /></button>
      <button type="button" className="account-menu-item" onClick={() => open("security")}><Settings size={18} /><span>设置</span><ChevronRight size={16} /></button>
      <hr />
      <button type="button" className="account-menu-item" onClick={() => { onClose(); onLogout(); }}><LogOut size={18} /><span>退出登录</span></button>
    </div>
  </div>, document.body);
}
